import React, { useState, useRef, useEffect } from 'react';
import { Upload, Filter, X, ChevronDown, Check } from './Icons';
import { Period, ChannelType } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

interface InboundNodeProps {
  label: string;
  value: string;
  onFilterClick: () => void;
  isFilterOpen: boolean;
}

const InboundNode: React.FC<InboundNodeProps> = ({ label, value, onFilterClick, isFilterOpen }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 relative">
      <div className="bg-white/5 dark:bg-white/[0.03] p-6 rounded-[24px] border border-brand-light dark:border-white/10 relative group">
        <div className="bg-brand-light dark:bg-[#E9F0D8] w-[260px] p-10 rounded-[24px] text-center border border-brand-medium/10 relative overflow-hidden transition-all duration-500 group-hover:scale-[1.02] shadow-[0_15px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
          <div className="w-12 h-12 bg-brand-dark/5 rounded-[14px] flex items-center justify-center mx-auto mb-8 border border-brand-dark/10">
            <Upload className="text-brand-medium" size={24} strokeWidth={2.5} />
          </div>
          <p className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-medium mb-3">{t('primary_ingestion')}</p>
          <div className="flex items-baseline justify-center gap-2 mb-10">
            <span className="text-6xl font-black tracking-tight text-brand-dark">{value}</span>
            <span className="text-[11px] font-black uppercase tracking-widest text-brand-dark/40">{t('files')}</span>
          </div>
          <div className="flex items-center justify-center space-x-2.5 bg-brand-dark py-2.5 px-6 rounded-full border border-white/10 mx-auto w-fit">
            <div className="w-2.5 h-2.5 bg-brand-bright rounded-full mr-1 shadow-[0_0_10px_rgba(87,181,171,0.8)]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-brand-light">{t('ready_to_route')}</span>
          </div>
        </div>
      </div>
      
      <button 
        onClick={onFilterClick}
        className={`flex flex-col items-center justify-center border px-8 py-3 rounded-[12px] shadow-sm transition-all group min-w-[140px] backdrop-blur-sm z-[20] ${
          isFilterOpen 
            ? 'bg-brand-medium border-brand-medium text-white ring-4 ring-brand-medium/10' 
            : 'bg-white dark:bg-white/[0.03] border-brand-light dark:border-white/10 hover:bg-brand-light/50 dark:hover:bg-white/[0.08]'
        }`}
      >
        <Filter size={18} className={`mb-1.5 transition-transform group-hover:scale-110 ${isFilterOpen ? 'text-white' : 'text-brand-medium dark:text-brand-bright'}`} />
        <span className={`text-[10px] font-black uppercase tracking-[0.2em] text-center leading-tight ${isFilterOpen ? 'text-white' : 'text-brand-medium dark:text-brand-bright'}`}>
          {t('advanced_filter')}
        </span>
      </button>
    </div>
  );
};

interface OutboundNodeProps {
  label: string;
  value: string;
  statusColor?: string;
}

const OutboundNode: React.FC<OutboundNodeProps> = ({ label, value, statusColor = '#71CFC4' }) => (
  <div className="bg-white dark:bg-white/[0.03] border border-brand-light dark:border-white/10 px-8 py-6 rounded-[14px] shadow-sm min-w-[260px] relative group hover:border-brand-medium/30 transition-all flex flex-col justify-center backdrop-blur-sm">
    <div className="flex items-center justify-between mb-2">
      <p className="text-[11px] font-black text-slate-400 dark:text-brand-bright uppercase tracking-widest">{label}</p>
      <div className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: statusColor }}></div>
    </div>
    <p className="text-3xl font-black text-brand-dark dark:text-brand-light tracking-tight">{value}</p>
  </div>
);

interface FlowDiagramProps {
  period?: Period | 'Custom';
  onPeriodChange?: (p: Period | 'Custom') => void;
  customNodes?: { label: string; value: string }[];
  customIngestionValue?: string;
}

const FlowDiagram: React.FC<FlowDiagramProps> = ({ period = 'Day', customNodes, customIngestionValue }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set(['eBill', 'EDI', 'eMail', 'Print']));
  const [selectedDocType, setSelectedDocType] = useState<string>('All');
  const [selectedDirection, setSelectedDirection] = useState<string>('Outbound');
  const filterRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const toggleChannel = (channel: string) => {
    const next = new Set(selectedChannels);
    if (next.has(channel)) next.delete(channel);
    else next.add(channel);
    setSelectedChannels(next);
  };

  const getMultiplier = () => {
    switch (period) {
      case '1h': return 1;
      case '4h': return 3.5;
      case 'Day': return 12;
      case 'Week': return 70;
      case 'Month': return 300;
      case 'Year': return 3600;
      case 'Custom': return 15;
      default: return 12;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen]);

  const m = getMultiplier();
  const val = (n: number) => Math.floor(n * m).toLocaleString().replace(/,/g, '.');

  const nodesToRender = customNodes || [
    { label: 'EDI CONNECTIVITY', value: val(1) },
    { label: 'EBILL NETWORK', value: val(241) },
    { label: 'DIGITAL EMAIL', value: val(46) },
    { label: 'PHYSICAL PRINT', value: val(20) }
  ];

  const ingestionVal = customIngestionValue || val(833);

  return (
    <div className="relative w-full bg-white dark:bg-brand-card-dark rounded-[24px] border border-brand-light dark:border-white/5 p-8 lg:p-16 overflow-visible min-h-[600px] flex items-center transition-colors duration-500">
      
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-light dark:bg-brand-bright/5 rounded-full blur-[140px] opacity-30 pointer-events-none -mr-40 -mt-40 transition-opacity"></div>
      
      <div className="relative w-full flex items-center justify-between gap-4 lg:gap-12 max-w-[1400px] mx-auto overflow-visible">
        
        <div className="flex-shrink-0 z-10 relative animate-in fade-in slide-in-from-left-8 duration-700 overflow-visible">
          <InboundNode 
            label={t('primary_ingestion')} 
            value={ingestionVal} 
            onFilterClick={() => setIsFilterOpen(!isFilterOpen)} 
            isFilterOpen={isFilterOpen}
          />

          {/* High-Contrast Advanced Filter Popup */}
          {isFilterOpen && (
            <div 
              ref={filterRef}
              className="absolute top-0 left-[calc(100%+24px)] w-[400px] bg-white dark:bg-black border border-gray-100 dark:border-white/20 rounded-[20px] shadow-[0_30px_70px_rgba(0,0,0,0.4)] dark:shadow-[0_30px_70px_rgba(0,0,0,0.9)] z-[110] animate-in fade-in zoom-in-95 slide-in-from-left-4 duration-300 overflow-hidden"
            >
              <div className="p-8 space-y-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Filter size={18} className="text-brand-medium dark:text-brand-bright" />
                    <h4 className="text-[14px] font-black uppercase tracking-[0.1em] text-brand-dark dark:text-brand-light">Filter Parameters</h4>
                  </div>
                  <button onClick={() => setIsFilterOpen(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors text-brand-dark dark:text-brand-bright">
                    <X size={20} strokeWidth={3} />
                  </button>
                </div>

                {/* Temporal Selection */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-medium dark:text-brand-medium">Time Period</label>
                  <div className="relative group">
                    <div className="w-full bg-gray-50 dark:bg-white/10 border border-gray-100 dark:border-white/20 rounded-xl px-4 py-3.5 text-sm font-bold text-brand-dark dark:text-brand-light flex items-center justify-between cursor-pointer group-hover:border-brand-medium transition-all">
                       <span>Last 24 Hours</span>
                       <ChevronDown size={14} className="text-brand-medium dark:text-brand-bright" strokeWidth={3} />
                    </div>
                  </div>
                </div>

                {/* Channel Filter */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-medium dark:text-brand-medium">Enabled Channels</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['eBill', 'EDI', 'eMail', 'Print', 'ePost'].map(chan => (
                      <button 
                        key={chan}
                        onClick={() => toggleChannel(chan)}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg border text-[11px] font-black uppercase transition-all ${
                          selectedChannels.has(chan) 
                            ? 'bg-brand-medium/10 border-brand-medium text-brand-medium dark:text-brand-light dark:border-brand-medium' 
                            : 'bg-white dark:bg-white/5 border-gray-100 dark:border-white/10 text-brand-medium dark:text-brand-medium/60'
                        }`}
                      >
                        {chan}
                        {selectedChannels.has(chan) && <Check size={12} strokeWidth={4} />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Doc Type Filter */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-medium dark:text-brand-medium">Document Category</label>
                  <div className="flex flex-wrap gap-2">
                    {['All', 'Invoice', 'Order', 'Note'].map(type => (
                      <button 
                        key={type}
                        onClick={() => setSelectedDocType(type)}
                        className={`px-5 py-2.5 rounded-full border text-[10px] font-black uppercase tracking-widest transition-all ${
                          selectedDocType === type 
                            ? 'bg-brand-dark text-white border-brand-dark dark:bg-brand-dark dark:text-brand-light dark:border-brand-light' 
                            : 'bg-white dark:bg-white/5 border-gray-100 dark:border-white/10 text-brand-medium dark:text-brand-medium/60'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Direction Filter */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-medium dark:text-brand-medium">Traffic Direction</label>
                  <div className="flex bg-gray-50 dark:bg-white/10 p-1 rounded-xl border border-gray-100 dark:border-white/20">
                    {['All', 'Inbound', 'Outbound'].map(dir => (
                      <button 
                        key={dir}
                        onClick={() => setSelectedDirection(dir)}
                        className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${
                          selectedDirection === dir 
                            ? 'bg-white dark:bg-white/20 text-brand-dark dark:text-brand-light shadow-md' 
                            : 'text-brand-medium dark:text-brand-medium/60'
                        }`}
                      >
                        {dir}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <button 
                    onClick={() => setIsFilterOpen(false)}
                    className="flex-1 border border-gray-100 dark:border-white/10 text-brand-medium dark:text-brand-medium py-4 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-white/10 transition-all"
                  >
                    Reset
                  </button>
                  <button 
                    onClick={() => setIsFilterOpen(false)}
                    className="flex-[2] bg-brand-dark dark:bg-brand-dark text-white dark:text-brand-light py-4 rounded-xl text-[11px] font-black uppercase tracking-[0.25em] shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all border dark:border-brand-light/30"
                  >
                    {t('update')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 h-[450px] relative pointer-events-none px-4 overflow-visible">
          <svg viewBox="0 0 600 400" className="w-full h-full overflow-visible" preserveAspectRatio="none">
            <defs>
              <linearGradient id="flow1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#8B9635', stopOpacity: 0.05 }} />
                <stop offset="100%" style={{ stopColor: '#8B9635', stopOpacity: 0.4 }} />
              </linearGradient>
            </defs>
            <path d="M 0 200 C 200 200, 400 45, 600 45" fill="none" stroke="url(#flow1)" strokeWidth="24" className="opacity-80 dark:opacity-60" strokeLinecap="round" />
            <path d="M 0 200 C 200 200, 400 148, 600 148" fill="none" stroke="rgba(71, 77, 34, 0.5)" strokeWidth="52" className="opacity-80 dark:opacity-70" strokeLinecap="round" />
            <path d="M 0 200 C 200 200, 400 252, 600 252" fill="none" stroke="rgba(87, 181, 171, 0.5)" strokeWidth="42" className="opacity-80 dark:opacity-70" strokeLinecap="round" />
            <path d="M 0 200 C 200 200, 400 355, 600 355" fill="none" stroke="url(#flow1)" strokeWidth="20" className="opacity-80 dark:opacity-60" strokeLinecap="round" />
          </svg>
        </div>

        <div className="flex flex-col gap-6 flex-shrink-0 z-10 animate-in fade-in slide-in-from-right-8 duration-700">
          {nodesToRender.map((node, i) => (
             <OutboundNode key={i} label={node.label} value={node.value} />
          ))}
        </div>
      </div>

      <div className="absolute top-10 left-10 flex items-center space-x-3 pointer-events-none">
         <div className="w-3 h-3 rounded-full bg-brand-bright shadow-[0_0_12px_rgba(87,181,171,0.5)]" />
         <span className="text-[11px] font-black text-brand-dark dark:text-brand-light uppercase tracking-[0.4em] opacity-80">{t('topology_monitoring')}</span>
      </div>
    </div>
  );
};

export default FlowDiagram;